import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  DashboardClientScope,
  DashboardUpcomingOpenEnrollmentItem
} from 'src/app/models/DashboardMetrics';
import { DashboardMetricsService } from 'src/app/services/dashboard-metrics.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { parseDateOnly } from 'src/app/utils/date-only';
import { getErrorMessage } from 'src/app/utils/error-message';

@UntilDestroy()
@Component({
  selector: 'vex-upcoming-open-enrollment-widget',
  templateUrl: './upcoming-open-enrollment-widget.component.html',
  styleUrls: ['./upcoming-open-enrollment-widget.component.scss']
})
export class UpcomingOpenEnrollmentWidgetComponent implements OnChanges {
  @Input() scope: DashboardClientScope = 'mine';

  /** Matches the dashboard list cap. A full page may hide later periods. */
  private readonly listLimit = 10;

  loading = false;
  items: DashboardUpcomingOpenEnrollmentItem[] = [];

  constructor(
    private dashboardMetricsService: DashboardMetricsService,
    private snackbarService: SnackbarService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scope']) {
      this.loadEnrollments();
    }
  }

  get emptyMessage(): string {
    return this.scope === 'all'
      ? 'No upcoming open enrollments.'
      : 'No upcoming open enrollments for your clients.';
  }

  get hasImminent(): boolean {
    return this.imminentCount > 0;
  }

  get summary(): string | null {
    if (this.loading || this.items.length === 0) {
      return null;
    }

    const soon = this.imminentCount;
    const later = this.items.length - soon;
    if (this.items.length >= this.listLimit) {
      if (soon > 0 && later > 0) {
        return `Showing the next ${this.listLimit}: ${this.periodLabel(soon)} within 7 days and ${this.periodLabel(later)} later.`;
      }
      if (soon > 0) {
        return `Showing the next ${this.listLimit}, all within 7 days.`;
      }
      return `Showing the next ${this.listLimit} upcoming open enrollments.`;
    }
    if (soon > 0 && later > 0) {
      return `${this.periodLabel(soon)} within 7 days and ${this.periodLabel(later)} later.`;
    }
    if (soon > 0) {
      return soon === 1
        ? '1 open enrollment in the next 7 days.'
        : `${soon} open enrollments in the next 7 days.`;
    }
    return later === 1
      ? '1 upcoming open enrollment.'
      : `${later} upcoming open enrollments.`;
  }

  private get imminentCount(): number {
    return this.items.filter(item => item.daysUntilOpen <= 7).length;
  }

  private periodLabel(count: number): string {
    return count === 1 ? '1 period' : `${count} periods`;
  }

  isGroupDivider(index: number): boolean {
    if (index <= 0) {
      return false;
    }
    return this.items[index - 1].daysUntilOpen <= 7 && this.items[index].daysUntilOpen > 7;
  }

  openDate(value: string): Date | null {
    return parseDateOnly(value);
  }

  daysLabel(days: number): string {
    if (days <= 0) {
      return 'today';
    }
    if (days === 1) {
      return 'in 1 day';
    }
    return `in ${days} days`;
  }

  private loadEnrollments(): void {
    this.loading = true;
    this.dashboardMetricsService.getUpcomingOpenEnrollments(this.scope)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result) => {
          this.items = result.items ?? [];
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.items = [];
          this.snackbarService.open(
            getErrorMessage(err, 'Failed to load upcoming open enrollments.'),
            'error'
          );
        }
      });
  }
}
